import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useDeleteDefect } from '@/models/article';
import { ArticleDefectWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useDatabaseTranslation } from '@/services/i18n';

import { DefectBrandResponsibilityToggle } from './DefectCard/BrandResponsibilityToggle';
import { DefectContextInput } from './DefectCard/DefectCardContext';
import { DefectPhotosInput } from './DefectCard/DefectCardPhotos';
import { DefectCustomDescriptionInput } from './DefectCard/DefectCustomDescriptionInput';
import {
  DefectExceptionalCoverageToggle,
  shouldShowExceptionalCoverageToggle,
} from './DefectCard/ExceptionalCoverageToggle';
import { ToBeRepairedToggle } from './DefectCard/ToBeRepairedToggle';

type CreateEditDefectDialogProps = {
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
  defect: ArticleDefectWithRelations | undefined;
  mode: 'create' | 'edit';
};

export const CreateEditDefectDialog = ({
  isOpen,
  setIsOpen,
  defect,
  mode,
}: CreateEditDefectDialogProps) => {
  const { article, request } = useArticleContext();
  const { t } = useLingui();

  const title =
    mode === 'edit'
      ? t({ id: 'create-defect-dialog.title.edit', message: 'Edit defect' })
      : t({ id: 'create-defect-dialog.title', message: 'Add a defect' });

  const { mutate: deleteDefect } = useDeleteDefect({
    articleId: article.id,
    requestId: request.id,
  });

  return (
    <Dialog
      title={title}
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        setIsOpen(newIsOpen);

        if (!newIsOpen && mode === 'create' && !!defect) {
          deleteDefect(defect.id);
        }
      }}
    >
      {!!defect && <CreateEditDefectForm onSave={() => setIsOpen(false)} defect={defect} />}
    </Dialog>
  );
};

const CreateEditDefectForm = ({
  onSave,
  defect,
}: {
  onSave: () => void;
  defect: ArticleDefectWithRelations;
}) => {
  const { _db } = useDatabaseTranslation();

  const { view, article } = useArticleContext();

  const defectType = defect.defectTypeOrganization ?? 'custom';

  const exceptionalCoverageToggleShown = shouldShowExceptionalCoverageToggle({
    view,
    defect,
    article,
  });

  return (
    <>
      <main>
        <Stack gap="1rem">
          {defectType === 'custom' && view.defects.fields.description.editable ? (
            <DefectCustomDescriptionInput defect={defect} />
          ) : (
            <Paragraph>
              {defectType === 'custom' ? defect.customDescription : _db(defectType.name)}
            </Paragraph>
          )}

          <hr />

          {view.defects.fields.photo.shown && view.defects.fields.photo.editable && (
            <DefectPhotosInput defect={defect} />
          )}

          {view.defects.fields.context.shown && view.defects.fields.context.editable && (
            <DefectContextInput defect={defect} />
          )}

          {view.defects.fields.brandResponsibility.enabled &&
            view.defects.fields.brandResponsibility.toggle.shown &&
            view.defects.fields.brandResponsibility.toggle.editable && (
              <DefectBrandResponsibilityToggle defect={defect} />
            )}

          {exceptionalCoverageToggleShown &&
            view.defects.fields.brandResponsibility.exceptionalCoverage.toggle.editable && (
              <DefectExceptionalCoverageToggle defect={defect} />
            )}

          {view.defects.fields.toBeRepaired.shown && view.defects.fields.toBeRepaired.editable && (
            <ToBeRepairedToggle defect={defect} />
          )}
        </Stack>
      </main>

      <Footer>
        <FooterButtons>
          <Button variant="primary" onPress={onSave}>
            <Trans id="create-defect-dialog.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
